import { RootState } from '../../../store'
import { createSelector } from '@reduxjs/toolkit'
import { AssignedTeamMember, AssignedTeamMemberReport } from './types'

export const selectAssignedTeamMemberReport = (state: RootState) => state.assignedTeamMemberPerTask.data
export const selectAssignedTeamMembersLoading = (state: RootState) =>
  state.assignedTeamMemberPerTask.loading
export const selectAssignedTeamMembersError = (state: RootState) => state.assignedTeamMemberPerTask.error
export const selectAssignedTeamMembersFilters = (state: RootState) =>
  state.assignedTeamMemberPerTask.filters

// Helper to determine if a task is overdue:
// - Not completed AND due date has passed
// - OR completed AFTER due date (for on-time rate purposes we treat late completion as overdue)
const isOverdue = (member: AssignedTeamMember): boolean => {
  if (!member.dueDate) return false
  const now = new Date()
  const due = new Date(member.dueDate)
  const status = member.taskStatus.toLowerCase()
  if (status === 'completed') {
    // Completed after due date => overdue
    if (member.completionDate) {
      return new Date(member.completionDate) > due
    }
    return false
  }
  // Not completed and due date has passed
  return now > due
}

export const selectFilteredAssignedTeamMembers = createSelector(
  [selectAssignedTeamMemberReport, selectAssignedTeamMembersFilters],
  (report: AssignedTeamMemberReport | null, filters) => {
    if (!report) return null

    return {
      ...report,
      assignedTeamMembers: report.assignedTeamMembers.filter((member: AssignedTeamMember) => {
        let matchesTaskStatus = true
        if (filters.taskStatus) {
          const statusFilter = filters.taskStatus.toLowerCase()
          if (statusFilter === 'overdue') {
            // 'overdue' is a derived state — use the isOverdue helper
            matchesTaskStatus = isOverdue(member)
          } else {
            matchesTaskStatus = statusFilter.includes(member.taskStatus.toLowerCase())
          }
        }

        const matchesJobProfile = !filters.jobProfile || member.jobProfile === filters.jobProfile
        const matchesFunctionalArea =
          !filters.functionalArea || member.functionalArea === filters.functionalArea
        const matchesSupervisor =
          !filters.supervisorName || member.supervisorName === filters.supervisorName
        const matchesOperationalManager =
          !filters.operationalManagerName ||
          member.operationalManagerName === filters.operationalManagerName
        const matchesTidsPractice = !filters.tidsPractice || member.tidsPractice === filters.tidsPractice

        const matchesSearch =
          !filters.searchQuery ||
          member.fullName.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
          member.email.toLowerCase().includes(filters.searchQuery.toLowerCase())

        return (
          matchesTaskStatus &&
          matchesJobProfile &&
          matchesFunctionalArea &&
          matchesSupervisor &&
          matchesTidsPractice &&
          matchesSearch &&
          matchesOperationalManager
        )
      }),
    }
  },
)

export const selectFilteredAssignedTeamMembersCount = createSelector(
  [selectFilteredAssignedTeamMembers],
  (filteredReport) => {
    if (!filteredReport)
      return {
        totalAssigned: 0,
        completed: { count: 0, percentage: 0 },
        notStarted: { count: 0, percentage: 0 },
        inProgress: { count: 0, percentage: 0 },
      }

    const totalAssigned = filteredReport.assignedTeamMembers.length

    const notStarted = filteredReport.assignedTeamMembers.filter(
      (member) => member.taskStatus.toLowerCase() === 'notstarted',
    )

    const completed = filteredReport.assignedTeamMembers.filter(
      (member) => member.taskStatus.toLowerCase() === 'completed',
    )

    const inProgressMembers = filteredReport.assignedTeamMembers.filter(
      (member) => member.taskStatus.toLowerCase() === 'inprogress',
    )

    const notStartedCount = notStarted.length
    const inProgressMembersCount = inProgressMembers.length
    const completedCount = completed.length

    const notStartedPercentage =
      totalAssigned > 0 ? ((notStartedCount / totalAssigned) * 100).toFixed(2) : 0
    const inProgressPercentage =
      totalAssigned > 0 ? ((inProgressMembersCount / totalAssigned) * 100).toFixed(2) : 0
    const completedPercentage =
      totalAssigned > 0 ? ((completedCount / totalAssigned) * 100).toFixed(2) : 0

    return {
      totalAssigned,
      notStarted: {
        count: notStartedCount,
        percentage: notStartedPercentage,
      },
      inProgress: {
        count: inProgressMembersCount,
        percentage: inProgressPercentage,
      },
      completed: {
        count: completedCount,
        percentage: completedPercentage,
      },
    }
  },
)

const isOnTime = (member: AssignedTeamMember): boolean => {
  if (member.taskStatus.toLowerCase() !== 'completed') return false
  if (!member.dueDate || !member.completionDate) return false
  return new Date(member.completionDate) <= new Date(member.dueDate)
}

export const selectTaskCompletionMetrics = createSelector(
  [selectFilteredAssignedTeamMembers],
  (filteredReport) => {
    if (!filteredReport) {
      return {
        onTimeCompletionRate: 0,
        overdue: { count: 0, percentage: 0 },
        pending: { count: 0, percentage: 0 },
        completed: { count: 0, percentage: 0 },
        inProgress: { count: 0, percentage: 0 },
        notStarted: { count: 0, percentage: 0 },
        totalAssigned: 0,
      }
    }

    const members = filteredReport.assignedTeamMembers
    const totalAssigned = members.length

    const completedMembers = members.filter((m) => m.taskStatus.toLowerCase() === 'completed')
    const completedCount = completedMembers.length

    const onTimeCount = members.filter(isOnTime).length
    const onTimeCompletionRate =
      totalAssigned > 0 ? parseFloat(((onTimeCount / totalAssigned) * 100).toFixed(2)) : 0

    const overdueMembers = members.filter(isOverdue)
    const overdueCount = overdueMembers.length
    const overduePercentage =
      totalAssigned > 0 ? parseFloat(((overdueCount / totalAssigned) * 100).toFixed(2)) : 0

    // Pending = Not Started + In Progress
    const pendingMembers = members.filter(
      (m) =>
        m.taskStatus.toLowerCase() === 'notstarted' ||
        m.taskStatus.toLowerCase() === 'inprogress',
    )
    const pendingCount = pendingMembers.length
    const pendingPercentage =
      totalAssigned > 0 ? parseFloat(((pendingCount / totalAssigned) * 100).toFixed(2)) : 0

    const inProgressCount = members.filter((m) => m.taskStatus.toLowerCase() === 'inprogress').length
    const notStartedCount = members.filter((m) => m.taskStatus.toLowerCase() === 'notstarted').length

    const completedPercentage =
      totalAssigned > 0 ? parseFloat(((completedCount / totalAssigned) * 100).toFixed(2)) : 0
    const inProgressPercentage =
      totalAssigned > 0 ? parseFloat(((inProgressCount / totalAssigned) * 100).toFixed(2)) : 0
    const notStartedPercentage =
      totalAssigned > 0 ? parseFloat(((notStartedCount / totalAssigned) * 100).toFixed(2)) : 0

    return {
      onTimeCompletionRate,
      overdue: { count: overdueCount, percentage: overduePercentage },
      pending: { count: pendingCount, percentage: pendingPercentage },
      completed: { count: completedCount, percentage: completedPercentage },
      inProgress: { count: inProgressCount, percentage: inProgressPercentage },
      notStarted: { count: notStartedCount, percentage: notStartedPercentage },
      totalAssigned,
    }
  },
)

export const selectTaskStatusOptions = createSelector([selectAssignedTeamMemberReport], () => {
  return ['Yes', 'No']
})

export const selectJobProfileOptions = createSelector([selectFilteredAssignedTeamMembers], (report) => {
  if (!report) return []
  return Array.from(new Set(report.assignedTeamMembers.map((member) => member.jobProfile))).sort()
})

export const selectFunctionalAreaOptions = createSelector(
  [selectFilteredAssignedTeamMembers],
  (report) => {
    if (!report) return []
    return Array.from(new Set(report.assignedTeamMembers.map((member) => member.functionalArea))).sort()
  },
)

export const selectSupervisorOptions = createSelector([selectFilteredAssignedTeamMembers], (report) => {
  if (!report) return []
  return Array.from(new Set(report.assignedTeamMembers.map((member) => member.supervisorName))).sort()
})

export const selectOperationalManagerOptions = createSelector(
  [selectFilteredAssignedTeamMembers],
  (report) => {
    if (!report) return []
    return Array.from(
      new Set(report.assignedTeamMembers.map((member) => member.operationalManagerName)),
    ).sort()
  },
)

export const selectTidsPracticeOptions = createSelector(
  [selectFilteredAssignedTeamMembers],
  (report) => {
    if (!report) return []
    return Array.from(new Set(report.assignedTeamMembers.map((member) => member.tidsPractice))).sort()
  },
)

// ─── Supervisor Drill-Down Selectors ─────────────────────────────────────────

export interface SupervisorStatusBreakdown {
  supervisorName: string
  total: number
  completed: number
  inProgress: number
  notStarted: number
  overdue: number
}

export const selectSupervisorBreakdowns = createSelector(
  [selectAssignedTeamMemberReport],
  (report): SupervisorStatusBreakdown[] => {
    if (!report) return []

    const map: Record<string, SupervisorStatusBreakdown> = {}

    for (const member of report.assignedTeamMembers) {
      const supervisor = member.supervisorName || 'Unassigned'
      if (!map[supervisor]) {
        map[supervisor] = {
          supervisorName: supervisor,
          total: 0,
          completed: 0,
          inProgress: 0,
          notStarted: 0,
          overdue: 0,
        }
      }
      map[supervisor].total++
      const status = member.taskStatus.toLowerCase()
      if (status === 'completed') {
        map[supervisor].completed++
      } else if (status === 'inprogress') {
        map[supervisor].inProgress++
      } else if (status === 'notstarted') {
        map[supervisor].notStarted++
      }
      if (isOverdue(member)) {
        map[supervisor].overdue++
      }
    }

    return Object.values(map).sort((a, b) => b.total - a.total)
  },
)

// Per-status → FA → supervisor drill-down selector
export interface DrillDownEntry {
  name: string
  count: number
}

// status → { fa → { supervisor → count } }
export type StatusFaSupervisorMap = Record<string, Record<string, Record<string, number>>>

export const selectStatusFaSupervisorDrillDown = createSelector(
  [selectAssignedTeamMemberReport],
  (report): StatusFaSupervisorMap => {
    if (!report) return {}

    const result: StatusFaSupervisorMap = {
      completed: {},
      inprogress: {},
      notstarted: {},
      overdue: {},
    }

    const addEntry = (
      statusKey: string,
      fa: string,
      supervisor: string,
    ) => {
      if (!result[statusKey][fa]) result[statusKey][fa] = {}
      result[statusKey][fa][supervisor] = (result[statusKey][fa][supervisor] || 0) + 1
    }

    for (const member of report.assignedTeamMembers) {
      const fa = member.functionalArea || 'Unassigned'
      const supervisor = member.supervisorName || 'Unassigned'
      const status = member.taskStatus.toLowerCase()

      if (status === 'completed') addEntry('completed', fa, supervisor)
      else if (status === 'inprogress') addEntry('inprogress', fa, supervisor)
      else if (status === 'notstarted') addEntry('notstarted', fa, supervisor)

      if (isOverdue(member)) addEntry('overdue', fa, supervisor)
    }

    return result
  },
)

// ─── New 4-level drill-down: TIDS Practice → Operational Manager → Functional Area → Supervisor ──
// tidsPractice → { operationalManager → { functionalArea → { supervisor → count } } }
export type TidsDrillDownMap = Record<string, Record<string, Record<string, Record<string, number>>>>

export const selectTidsDrillDownMap = createSelector(
  [selectAssignedTeamMemberReport],
  (report): TidsDrillDownMap => {
    if (!report) return {}

    const result: TidsDrillDownMap = {}

    for (const member of report.assignedTeamMembers) {
      const tidsPractice = member.tidsPractice || 'Unassigned'
      const opManager = member.operationalManagerName || 'Unassigned'
      const fa = member.functionalArea || 'Unassigned'
      const supervisor = member.supervisorName || 'Unassigned'

      if (!result[tidsPractice]) result[tidsPractice] = {}
      if (!result[tidsPractice][opManager]) result[tidsPractice][opManager] = {}
      if (!result[tidsPractice][opManager][fa]) result[tidsPractice][opManager][fa] = {}
      result[tidsPractice][opManager][fa][supervisor] =
        (result[tidsPractice][opManager][fa][supervisor] || 0) + 1
    }

    return result
  },
)

// ─── 5-level drill-down: Status → TIDS Practice → Operational Manager → Functional Area → Supervisor ──
// status → { tidsPractice → { operationalManager → { functionalArea → { supervisor → count } } } }
export type StatusTidsDrillDownMap = Record<string, TidsDrillDownMap>

export const selectStatusTidsDrillDownMap = createSelector(
  [selectAssignedTeamMemberReport],
  (report): StatusTidsDrillDownMap => {
    if (!report) return {}

    const result: StatusTidsDrillDownMap = {
      Completed: {},
      'In Progress': {},
      'Not Started': {},
      Overdue: {},
    }

    const addEntry = (
      statusKey: string,
      tids: string,
      opManager: string,
      fa: string,
      supervisor: string,
    ) => {
      if (!result[statusKey]) result[statusKey] = {}
      if (!result[statusKey][tids]) result[statusKey][tids] = {}
      if (!result[statusKey][tids][opManager]) result[statusKey][tids][opManager] = {}
      if (!result[statusKey][tids][opManager][fa]) result[statusKey][tids][opManager][fa] = {}
      result[statusKey][tids][opManager][fa][supervisor] =
        (result[statusKey][tids][opManager][fa][supervisor] || 0) + 1
    }

    for (const member of report.assignedTeamMembers) {
      const tids = member.tidsPractice || 'Unassigned'
      const opManager = member.operationalManagerName || 'Unassigned'
      const fa = member.functionalArea || 'Unassigned'
      const supervisor = member.supervisorName || 'Unassigned'
      const status = member.taskStatus.toLowerCase()

      if (status === 'completed') addEntry('Completed', tids, opManager, fa, supervisor)
      else if (status === 'inprogress') addEntry('In Progress', tids, opManager, fa, supervisor)
      else if (status === 'notstarted') addEntry('Not Started', tids, opManager, fa, supervisor)

      if (isOverdue(member)) addEntry('Overdue', tids, opManager, fa, supervisor)
    }

    return result
  },
)

// Helper function to calculate group statistics
const calculateGroupStatistics = (
  members: AssignedTeamMember[],
  groupKey: keyof AssignedTeamMember,
): Record<string, { total: number; inProgress: number; notStarted: number; completed: number }> => {
  return members.reduce(
    (acc, member) => {
      const groupName = member[groupKey] as string

      if (!acc[groupName]) {
        acc[groupName] = { total: 0, inProgress: 0, notStarted: 0, completed: 0 }
      }

      acc[groupName].total++

      if ( member.taskStatus.toLowerCase() === 'inprogress' ) {
        acc[groupName].inProgress++
      } else if (member.taskStatus.toLowerCase() === 'notstarted') {
        acc[groupName].notStarted++
      } else if (member.taskStatus.toLowerCase() === 'completed') {
        acc[groupName].completed++
      }

      return acc
    },
    {} as Record<string, { total: number; inProgress: number; notStarted: number; completed: number }>,
  )
}

export const selectGroupedTeamMemberReport = createSelector(
  [selectFilteredAssignedTeamMembers, selectAssignedTeamMembersFilters],
  (filteredReport, filters) => {
    if (!filteredReport) return {}

    // Check if no filters are applied
    const hasNoFilters = Object.values(filters).every((filter) => filter === undefined)

    // If no filters, default to TIDS Practice
    if (hasNoFilters) {
      return {
        data: calculateGroupStatistics(filteredReport.assignedTeamMembers, 'tidsPractice'),
      }
    }

    // If filtered by TIDS Practice only, show operational managers
    if (filters.tidsPractice && !filters.operationalManagerName) {
      const filteredByOperationalManager = filteredReport.assignedTeamMembers.filter(
        (member) => member.tidsPractice === filters.tidsPractice,
      )

      return {
        data: calculateGroupStatistics(filteredByOperationalManager, 'operationalManagerName'),
      }
    }

    // If filtered by operational manager only, show supervisors
    if (filters.operationalManagerName && !filters.supervisorName) {
      const filteredBySupervisor = filteredReport.assignedTeamMembers.filter(
        (member) => member.operationalManagerName === filters.operationalManagerName,
      )

      return {
        data: calculateGroupStatistics(filteredBySupervisor, 'supervisorName'),
      }
    }

    // Default grouping
    return {
      data: calculateGroupStatistics(filteredReport.assignedTeamMembers, 'tidsPractice'),
    }
  },
)
